import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
    AutoComplete,
    AutoCompleteChangeEvent,
    AutoCompleteCompleteEvent,
    AutoCompleteProps,
    AutoCompleteSelectEvent,
} from "primereact/autocomplete";
import { classNames } from "primereact/utils";
import { DashlyFieldShell } from "../shared/DashlyFieldShell";
import { useDashlyOverlayAppendTo } from "../shared/resolveDashlyOverlayAppendTo";
import { DashlyControlSize, dashlyControlSizeClass } from "../shared/dashlyControlSize";
import { DashlyLeadingIconSlot, resolveLeadingIconClass } from "../shared/resolveLeadingIcon";
import type { DashlyLeadingIcon } from "../shared/resolveLeadingIcon";
import { useDashlyFieldValidation } from "../shared/useDashlyFieldValidation";
import type { FieldValidationRules } from "../../../Util/fieldValidation";
import { clampFieldText } from "../../../Util/fieldValidation";
import "../shared/dashlyFormControl.scss";
import "./DashlyAutoComplete.scss";
import "../EditableAutoComplete/EditableAutoComplete.scss";

type DashlyAutoCompleteSharedProps = Omit<
    AutoCompleteProps,
    "value" | "onChange" | "completeMethod" | "suggestions" | "multiple" | "field" | "variant"
> & {
    options?: unknown[];
    optionLabel?: string;
    searchFields?: string[];
    leadingIcon?: DashlyLeadingIcon;
    allowCustomValue?: boolean;
    /** Open the suggestion list with all options when the input is focused. */
    showSuggestionsOnFocus?: boolean;
    /** `processflow` reuses compact styling from legacy EditableAutoComplete. */
    variant?: "form" | "processflow";
    /** Control height scale. Default `md` (2.5rem). */
    controlSize?: DashlyControlSize;
    label?: React.ReactNode;
    labelInfo?: string;
    hint?: React.ReactNode;
    required?: boolean;
    containerClassName?: string;
    /** Entity-aligned validation (maxLength, minLength, required, min, max). */
    validation?: FieldValidationRules;
    /** Show a spinner inside the control while async options are loading. */
    loading?: boolean;
};

export type DashlyAutoCompleteSingleProps = DashlyAutoCompleteSharedProps & {
    multiple?: false;
    value?: string | null;
    onChange: (value: string) => void;
};

export type DashlyAutoCompleteMultipleProps = DashlyAutoCompleteSharedProps & {
    multiple: true;
    value?: string[];
    onChange: (value: string[]) => void;
    keepOpenOnSelect?: boolean;
};

export type DashlyAutoCompleteProps = DashlyAutoCompleteSingleProps | DashlyAutoCompleteMultipleProps;

const isMultipleProps = (props: DashlyAutoCompleteProps): props is DashlyAutoCompleteMultipleProps =>
    props.multiple === true;

const DashlyAutoComplete = forwardRef<AutoComplete, DashlyAutoCompleteProps>(function DashlyAutoComplete(
    props,
    ref
) {
    const {
        options = [],
        optionLabel = "value",
        searchFields,
        leadingIcon,
        allowCustomValue = false,
        showSuggestionsOnFocus = false,
        variant: variantProp,
        controlSize = "md",
        label,
        labelInfo,
        hint,
        required,
        containerClassName,
        validation,
        loading = false,
        className,
        panelClassName,
        itemTemplate,
        selectedItemTemplate,
        placeholder,
        disabled,
        inputId,
        id,
        onFocus,
        onBlur,
        onKeyDown,
        minLength = 0,
        maxLength: maxLengthProp,
        /* eslint-disable @typescript-eslint/no-unused-vars -- excluded from ...rest */
        onChange: _onChange,
        value: _value,
        multiple: _multiple,
        /* eslint-enable @typescript-eslint/no-unused-vars */
        appendTo: appendToProp,
        ...rest
    } = props;

    const isMultiple = isMultipleProps(props);
    const { error, invalid, markTouched, resolvedMaxLength } = useDashlyFieldValidation(
        props.value,
        validation,
        { multiple: isMultiple },
    );
    const effectiveMaxLength = maxLengthProp ?? resolvedMaxLength;

    const clampValue = (raw: string) => clampFieldText(raw, effectiveMaxLength);

    const variant: "form" | "processflow" = variantProp ?? "form";
    const resolvedInputId = inputId ?? id;
    const internalRef = useRef<AutoComplete | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const committedValueRef = useRef("");
    const committedAtRef = useRef(0);

    const setAutoCompleteRef = (instance: AutoComplete | null) => {
        internalRef.current = instance;
        if (typeof ref === "function") {
            ref(instance);
        } else if (ref) {
            ref.current = instance;
        }
    };
    const overlayAppendTo = useDashlyOverlayAppendTo(wrapperRef);
    const resolvedAppendTo = appendToProp ?? overlayAppendTo;
    const [internalOptions, setInternalOptions] = useState<unknown[]>(options);
    const [suggestions, setSuggestions] = useState<unknown[]>([]);

    const singleValue = !isMultiple ? (props as DashlyAutoCompleteSingleProps).value : undefined;

    useEffect(() => {
        setInternalOptions(options);
    }, [options]);

    useEffect(() => {
        if (!isMultiple) {
            committedValueRef.current = `${singleValue ?? ""}`.trim();
        }
    }, [isMultiple, singleValue]);

    const leadingIconClass = resolveLeadingIconClass(
        leadingIcon,
        isMultiple ? props.value : props.value
    );
    const hasValue = isMultiple
        ? (props.value || []).some((entry) => `${entry}`.trim())
        : Boolean(`${props.value ?? ""}`.trim());
    const showLoadingPlaceholder = loading && !hasValue;
    const resolvedPlaceholder = showLoadingPlaceholder ? "" : placeholder;

    const runSearch = (rawQuery: string) => {
        const query = rawQuery.trim();
        const lowerQuery = query.toLowerCase();
        const fieldsToSearch = searchFields?.length ? searchFields : [optionLabel];

        let filtered =
            query.length === 0
                ? [...internalOptions]
                : internalOptions.filter((opt) =>
                      fieldsToSearch.some((field) =>
                          ((opt as Record<string, unknown>)[field]?.toString().toLowerCase() ?? "").includes(
                              lowerQuery
                          )
                      )
                  );

        if (allowCustomValue && query) {
            const exactMatch = internalOptions.some((opt) =>
                fieldsToSearch.some(
                    (field) =>
                        (opt as Record<string, unknown>)[field]?.toString().toLowerCase() === lowerQuery
                )
            );
            if (!exactMatch) {
                filtered = [{ [optionLabel]: query }, ...filtered];
            }
        }

        setSuggestions(filtered);
    };

    const search = (event: AutoCompleteCompleteEvent) => {
        runSearch(event.query ?? "");
    };

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        markTouched();
        onBlur?.(event);
    };

    const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        onFocus?.(event);
        if (showSuggestionsOnFocus) {
            runSearch("");
        }
    };

    const handleWrapKeyDownCapture = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!isMultiple || !allowCustomValue || event.key !== "Enter") {
            return;
        }

        const panelClass = panelClassName
            ?.split(/\s+/)
            .find((token) => token.endsWith("-panel") || token.includes("autocomplete-panel"));
        const highlighted = panelClass
            ? document.querySelector(
                  `.p-autocomplete-panel.${panelClass} .p-autocomplete-item[data-p-highlight="true"]`
              )
            : document.querySelector(".p-autocomplete-panel .p-autocomplete-item[data-p-highlight=\"true\"]");

        if (highlighted) {
            return;
        }

        const input = wrapperRef.current?.querySelector(
            ".p-autocomplete-input-token input"
        ) as HTMLInputElement | null;
        const raw = input?.value?.trim();
        if (!raw) {
            return;
        }

        const nextValue = clampValue(raw);
        const current = props.value ?? [];
        if (current.some((entry) => entry.toLowerCase() === nextValue.toLowerCase())) {
            if (input) {
                input.value = "";
            }
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        props.onChange([...current, nextValue]);
        markTouched();
        if (input) {
            input.value = "";
        }
        setSuggestions([]);
        event.preventDefault();
        event.stopPropagation();
    };

    const resolvedItemTemplate = useMemo(() => {
        if (itemTemplate) {
            return itemTemplate;
        }
        if (!leadingIcon) {
            return undefined;
        }
        return (option: unknown) => {
            const iconClass = resolveLeadingIconClass(leadingIcon, option);
            const labelText = (option as Record<string, unknown>)?.[optionLabel];
            return (
                <span className="dashly-autocomplete__item">
                    <DashlyLeadingIconSlot iconClass={iconClass} />
                    <span>{labelText != null ? String(labelText) : ""}</span>
                </span>
            );
        };
    }, [itemTemplate, leadingIcon, optionLabel]);

    const handleMultipleChange = (event: AutoCompleteChangeEvent) => {
        if (!isMultiple) {
            return;
        }
        markTouched();
        const selectedValues = Array.isArray(event.value)
            ? event.value
            : event.value == null
              ? []
              : [event.value];

        const objectValues = selectedValues.map((val: string | Record<string, unknown>) => {
            if (typeof val === "string") {
                const normalized = clampValue(val);
                const exists = internalOptions.find(
                    (opt) =>
                        (opt as Record<string, unknown>)[optionLabel]?.toString().toLowerCase() ===
                        normalized.toLowerCase()
                );
                return exists ?? { [optionLabel]: normalized };
            }
            const labelValue = clampValue(String((val as Record<string, unknown>)[optionLabel] ?? ""));
            return { [optionLabel]: labelValue };
        });

        if (allowCustomValue) {
            objectValues.forEach((val) => {
                const exists = internalOptions.some(
                    (opt) =>
                        (opt as Record<string, unknown>)[optionLabel]?.toString().toLowerCase() ===
                        (val as Record<string, unknown>)[optionLabel]?.toString().toLowerCase()
                );
                if (!exists) {
                    setInternalOptions((previous) => [...previous, val]);
                }
            });
        }

        props.onChange(objectValues.map((val) => String((val as Record<string, unknown>)[optionLabel] ?? "")));

        if (props.keepOpenOnSelect && !disabled) {
            window.setTimeout(() => {
                runSearch("");
                internalRef.current?.show();
                internalRef.current?.focus();
            }, 0);
        }
    };

    const rememberCustomOption = (labelValue: string) => {
        if (!allowCustomValue || !labelValue) {
            return;
        }
        const exists = internalOptions.some(
            (opt) =>
                String((opt as Record<string, unknown>)[optionLabel] ?? "").toLowerCase() ===
                labelValue.toLowerCase()
        );
        if (!exists) {
            setInternalOptions((previous) => [...previous, { [optionLabel]: labelValue }]);
        }
    };

    const commitSingleValue = (raw: unknown) => {
        if (isMultiple || raw == null) {
            return "";
        }
        const labelValue =
            typeof raw === "string"
                ? clampValue(raw)
                : clampValue(String((raw as Record<string, unknown>)[optionLabel] ?? ""));
        rememberCustomOption(labelValue);
        committedValueRef.current = labelValue;
        committedAtRef.current = Date.now();
        (props as DashlyAutoCompleteSingleProps).onChange(labelValue);
        return labelValue;
    };

    const resolveSingleDisplayValue = (raw: string | null | undefined): string | Record<string, unknown> => {
        const text = `${raw ?? ""}`.trim();
        if (!text) {
            return "";
        }
        const match = internalOptions.find(
            (opt) =>
                String((opt as Record<string, unknown>)[optionLabel] ?? "").toLowerCase() === text.toLowerCase()
        ) as Record<string, unknown> | undefined;
        return match ?? { [optionLabel]: text };
    };

    const handleSingleSelect = (event: AutoCompleteSelectEvent) => {
        if (isMultiple) {
            return;
        }
        markTouched();
        commitSingleValue(event.value);
    };

    const handleSingleChange = (event: AutoCompleteChangeEvent) => {
        if (isMultiple) {
            return;
        }
        markTouched();
        const next = event.value;
        if (next == null || (typeof next === "string" && !next.trim())) {
            const recentlyCommitted =
                committedValueRef.current &&
                Date.now() - committedAtRef.current < 400;
            if (recentlyCommitted) {
                return;
            }
            committedValueRef.current = "";
            (props as DashlyAutoCompleteSingleProps).onChange("");
            return;
        }
        commitSingleValue(next);
    };

    const resolvedSingleValue = isMultiple
        ? ""
        : resolveSingleDisplayValue((props as DashlyAutoCompleteSingleProps).value);

    const autoComplete = isMultiple ? (
        <AutoComplete
            ref={setAutoCompleteRef}
            multiple
            value={(props.value || []).map((entry: string) => ({
                [optionLabel]: entry,
            }))}
            suggestions={suggestions}
            completeMethod={search}
            field={optionLabel}
            dropdown
            forceSelection={!allowCustomValue}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            itemTemplate={resolvedItemTemplate}
            selectedItemTemplate={selectedItemTemplate}
            className={classNames(
                "dashly-autocomplete",
                variant === "processflow" && "editable-autocomplete--processflow",
                invalid && "p-invalid",
                className
            )}
            panelClassName={classNames(
                "dashly-autocomplete-panel",
                variant === "processflow" && "editable-autocomplete-panel--processflow",
                panelClassName
            )}
            onChange={handleMultipleChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={onKeyDown}
            minLength={minLength}
            maxLength={effectiveMaxLength}
            appendTo={resolvedAppendTo}
            aria-invalid={invalid || undefined}
            {...rest}
        />
    ) : (
        <AutoComplete
            ref={setAutoCompleteRef}
            value={resolvedSingleValue}
            suggestions={suggestions}
            completeMethod={search}
            field={optionLabel}
            dropdown
            forceSelection={!allowCustomValue}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            itemTemplate={resolvedItemTemplate}
            className={classNames(
                "dashly-autocomplete",
                variant === "processflow" && "editable-autocomplete--processflow",
                invalid && "p-invalid",
                className
            )}
            panelClassName={classNames(
                "dashly-autocomplete-panel",
                variant === "processflow" && "editable-autocomplete-panel--processflow",
                panelClassName
            )}
            onChange={handleSingleChange}
            onSelect={handleSingleSelect}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={onKeyDown}
            minLength={minLength}
            maxLength={effectiveMaxLength}
            appendTo={resolvedAppendTo}
            aria-invalid={invalid || undefined}
            {...rest}
        />
    );

    const control = (
        <div
            ref={wrapperRef}
            className={classNames(
                "dashly-autocomplete-wrap",
                dashlyControlSizeClass(controlSize),
                leadingIconClass && "dashly-autocomplete-wrap--with-icon",
                showLoadingPlaceholder && "dashly-autocomplete-wrap--loading",
                disabled && "dashly-autocomplete-wrap--disabled",
                invalid && "dashly-autocomplete-wrap--invalid",
            )}
            onKeyDownCapture={handleWrapKeyDownCapture}
            aria-busy={loading || undefined}
        >
            {leadingIconClass ? (
                <DashlyLeadingIconSlot
                    iconClass={leadingIconClass}
                    className="dashly-autocomplete-wrap__leading-icon"
                />
            ) : null}
            {autoComplete}
            {showLoadingPlaceholder && placeholder ? (
                <div className="dashly-autocomplete-wrap__loading-placeholder" aria-hidden>
                    <i className="pi pi-spinner pi-spin" />
                    <span>{placeholder}</span>
                </div>
            ) : null}
        </div>
    );

    if (label || labelInfo || hint || error) {
        return (
            <DashlyFieldShell
                label={label}
                labelInfo={labelInfo}
                hint={hint}
                error={error}
                htmlFor={resolvedInputId}
                required={required ?? validation?.required}
                className={containerClassName}
            >
                {control}
            </DashlyFieldShell>
        );
    }

    if (error) {
        return (
            <div className={classNames("dashly-field-shell dashly-field-shell--invalid", containerClassName)}>
                {control}
                <span className="dashly-field-shell__error" role="alert">
                    {error}
                </span>
            </div>
        );
    }

    return control;
});

DashlyAutoComplete.displayName = "DashlyAutoComplete";

export { DashlyAutoComplete };
